import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  PlayArrow as StartIcon, 
  Save as SaveIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon 
} from '@mui/icons-material';
import { visitAPI, optometryAPI, patientAPI } from '../services/api';
import OptometryWorkupForm from '../components/OptometryWorkupForm';

const OptometristPortal = () => {
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [patientHistory, setPatientHistory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [optometryForm, setOptometryForm] = useState({
    visitNumber: '',
    optometrist: '',
    paediatricAssistant: '',
    systemicHistory: {
      hypertension: { present: false, since: '' },
      diabetes: { present: false, since: '' },
      thyroid: { present: false, since: '' },
      cardiacProblem: false,
      others: '',
      allergySensitivity: ''
    },
    vitals: {
      bloodPressure: { systolic: '', diastolic: '' },
      bloodSugar: '',
      bodyWeight: '',
      adrPlus: ''
    },
    ocularHistory: '',
    visualAcuity: {
      right: { ucva: '', vacgl: '', cph: '' },
      left: { ucva: '', vacgl: '', cph: '' },
      amslerChart: { right: '', left: '' },
      colorVision: ''
    },
    ownGlasses: {
      right: { sphere: '', cylinder: '', axis: '', va: '' },
      left: { sphere: '', cylinder: '', axis: '', va: '' },
      chartD: 'Snellen',
      ne: 'N',
      bifocalSep: 'B',
      iopMethod: 'NCT',
      formula: 'DRESENER CO.',
      time: ''
    },
    autoRefractor: {
      right: { sphere: '', cylinder: '', axis: '' },
      left: { sphere: '', cylinder: '', axis: '' }
    },
    cdr: { right: '', left: '' },
    retinoscopyDryEye: {
      right: { sphere: '', cylinder: '', axis: '' },
      left: { sphere: '', cylinder: '', axis: '' }
    },
    refraction: {
      right: { distance: '', nearAdd: '', nearPrescription: '' },
      left: { distance: '', nearAdd: '', nearPrescription: '' },
      sendSMS: false,
      bifocalSep: 'B',
      technician: ''
    },
    recommendations: '',
    optometristRemarks: '',
    correctedIOP: {
      right: { cornealThickness: '', measuredIOP: '', dilatedIOP: '', afterRxIOP: '' },
      left: { cornealThickness: '', measuredIOP: '', dilatedIOP: '', afterRxIOP: '' },
      biometryType: 'optical'
    },
    opticalBiometry: {
      right: {
        k1Flat: '', k2Steep: '', kDifference: '', axisK1: '', axisK2: '',
        axialWW: '', acd: '', ltLens: '', aConst: '.118', actualACD: '',
        iolPower: '', iolType: '', postOp: '', method: ''
      },
      left: {
        k1Flat: '', k2Steep: '', kDifference: '', axisK1: '', axisK2: '',
        axialWW: '', acd: '', ltLens: '', aConst: '.118', actualACD: '',
        iolPower: '', iolType: '', postOp: '', method: ''
      },
      biometryStage: '',
      biometryDoneBy: '',
      powerFormula1: 'SRK-T',
      powerFormula2: 'SRK-T'
    }
  });

  useEffect(() => {
    fetchWaitingPatients();
    fetchAllPatients();
  }, []);

  const fetchWaitingPatients = async () => {
    try {
      const response = await visitAPI.getVisits({ status: 'waiting-optometrist' });
      setVisits(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch waiting patients', 'error');
    }
  };

  const fetchAllPatients = async () => {
    try {
      const response = await patientAPI.getPatients({ limit: 100 });
      setPatients(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch patients', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewHistory = async (visit) => {
    try {
      const response = await patientAPI.getPatientHistory(visit.patient._id);
      setPatientHistory(response.data.data);
      setOpenHistoryDialog(true);
    } catch (error) {
      showSnackbar('Failed to fetch patient history', 'error');
    }
  };

  const handleStartOptometry = async (visit) => {
    try {
      // Start the optometrist stage
      await visitAPI.startStage(visit._id, { stage: 'optometrist' });
      setSelectedVisit(visit);
      setOpenDialog(true);
      showSnackbar('Optometry started - Fill the form to complete', 'info');
      // Keep patient in the list - they're in-progress, not removed
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to start optometry', 'error');
    }
  };

  const handleSaveOptometry = async () => {
    try {
      // Save optometry record
      await optometryAPI.createRecord({
        visit: selectedVisit._id,
        ...optometryForm,
      });
      
      // End optometrist stage (this calculates time spent)
      await visitAPI.endStage(selectedVisit._id, { notes: 'Optometry completed' });
      
      // Auto-refer to doctor
      await visitAPI.updateVisit(selectedVisit._id, {
        status: 'waiting-doctor',
        referredTo: 'doctor'
      });
      
      showSnackbar('Optometry completed! Patient referred to doctor.', 'success');
      setOpenDialog(false);
      fetchWaitingPatients(); // Refresh list - patient removed after save
      resetForm();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save record', 'error');
    }
  };

  const handleCancelOptometry = async () => {
    try {
      // If user cancels without saving, end the stage
      if (selectedVisit) {
        await visitAPI.endStage(selectedVisit._id, { notes: 'Optometry cancelled' });
        // Reset visit status back to waiting
        await visitAPI.updateVisit(selectedVisit._id, {
          status: 'waiting-optometrist'
        });
      }
      setOpenDialog(false);
      resetForm();
      fetchWaitingPatients(); // Refresh to show patient back in waiting list
      showSnackbar('Optometry cancelled', 'info');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to cancel', 'error');
    }
  };

  const resetForm = () => {
    setOptometryForm({
      visitNumber: '',
      optometrist: '',
      paediatricAssistant: '',
      systemicHistory: {
        hypertension: { present: false, since: '' },
        diabetes: { present: false, since: '' },
        thyroid: { present: false, since: '' },
        cardiacProblem: false,
        others: '',
        allergySensitivity: ''
      },
      vitals: {
        bloodPressure: { systolic: '', diastolic: '' },
        bloodSugar: '',
        bodyWeight: '',
        adrPlus: ''
      },
      ocularHistory: '',
      visualAcuity: {
        right: { ucva: '', vacgl: '', cph: '' },
        left: { ucva: '', vacgl: '', cph: '' },
        amslerChart: { right: '', left: '' },
        colorVision: ''
      },
      ownGlasses: {
        right: { sphere: '', cylinder: '', axis: '', va: '' },
        left: { sphere: '', cylinder: '', axis: '', va: '' },
        chartD: 'Snellen',
        ne: 'N',
        bifocalSep: 'B',
        iopMethod: 'NCT',
        formula: 'DRESENER CO.',
        time: ''
      },
      autoRefractor: {
        right: { sphere: '', cylinder: '', axis: '' },
        left: { sphere: '', cylinder: '', axis: '' }
      },
      cdr: { right: '', left: '' },
      retinoscopyDryEye: {
        right: { sphere: '', cylinder: '', axis: '' },
        left: { sphere: '', cylinder: '', axis: '' }
      },
      refraction: {
        right: { distance: '', nearAdd: '', nearPrescription: '' },
        left: { distance: '', nearAdd: '', nearPrescription: '' },
        sendSMS: false,
        bifocalSep: 'B',
        technician: ''
      },
      recommendations: '',
      optometristRemarks: '',
      correctedIOP: {
        right: { cornealThickness: '', measuredIOP: '', dilatedIOP: '', afterRxIOP: '' },
        left: { cornealThickness: '', measuredIOP: '', dilatedIOP: '', afterRxIOP: '' },
        biometryType: 'optical'
      },
      opticalBiometry: {
        right: {
          k1Flat: '', k2Steep: '', kDifference: '', axisK1: '', axisK2: '',
          axialWW: '', acd: '', ltLens: '', aConst: '.118', actualACD: '',
          iolPower: '', iolType: '', postOp: '', method: ''
        },
        left: {
          k1Flat: '', k2Steep: '', kDifference: '', axisK1: '', axisK2: '',
          axialWW: '', acd: '', ltLens: '', aConst: '.118', actualACD: '',
          iolPower: '', iolType: '', postOp: '', method: ''
        },
        biometryStage: '',
        biometryDoneBy: '',
        powerFormula1: 'SRK-T',
        powerFormula2: 'SRK-T'
      }
    });
  };

  const filteredPatients = patients.filter(patient => 
    patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.contact?.phone?.includes(searchQuery)
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          Optometrist Portal
        </Typography>
        <IconButton onClick={() => { fetchWaitingPatients(); fetchAllPatients(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label={`Waiting Patients (${visits.length})`} />
        <Tab label="All Patients" />
      </Tabs>

      {tabValue === 0 && (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Age / Gender</TableCell>
                <TableCell>Chief Complaint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No patients waiting for optometry</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit) => (
                  <TableRow key={visit._id} hover>
                    <TableCell>
                      <Chip
                        label={`#${visit.tokenNumber || '-'}`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 800, fontSize: '0.9rem', minWidth: 44 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{visit.patient?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {visit.patient?.patientId} • {visit.visitId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.patient?.age} yrs / {visit.patient?.gender}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.chiefComplaint || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="Waiting" color="warning" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<StartIcon />}
                        onClick={() => handleStartOptometry(visit)}
                        sx={{ mr: 1, textTransform: 'none' }}
                      >
                        Start Optometry
                      </Button>
                      <IconButton size="small" onClick={() => handleViewHistory(visit)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {tabValue === 1 && (
        <Box>
          <TextField
            fullWidth
            placeholder="Search by name, patient ID, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={3}>
            {filteredPatients.map((patient) => (
              <Grid item xs={12} md={6} lg={4} key={patient._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {patient.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {patient.patientId}
                        </Typography>
                      </Box>
                      <Chip label="Patient" color="info" size="small" />
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Age:</strong> {patient.age} • <strong>Gender:</strong> {patient.gender}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Phone:</strong> {patient.contact?.phone || 'N/A'}
                    </Typography>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleViewHistory({ patient })}
                    >
                      View History
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Optometry Workup Form Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCancelOptometry}
        maxWidth="lg" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Optometrist Workup Sheet - {selectedVisit?.patient?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <OptometryWorkupForm 
            patient={selectedVisit?.patient} 
            formData={optometryForm} 
            setFormData={setOptometryForm} 
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCancelOptometry} 
            size="large"
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveOptometry} 
            variant="contained" 
            size="large" 
            color="success"
            startIcon={<SaveIcon />}
          >
            Save & Refer to Doctor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient History Dialog */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Patient History - {patientHistory?.optometryRecords?.[0]?.patient?.name || 'N/A'}
        </DialogTitle>
        <DialogContent>
          {patientHistory && (
            <Box>
              {/* Optometry Records */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Previous Optometry Records</Typography>
              {patientHistory.optometryRecords?.length > 0 ? (
                patientHistory.optometryRecords.map((record, index) => (
                  <Accordion key={record._id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        Visit {record.visit?.visitId} - {new Date(record.visit?.visitDate).toLocaleDateString()} 
                        {record.performedBy?.name && ` - By ${record.performedBy.name}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Visual Acuity (Right)</strong></TableCell>
                            <TableCell>UCVA: {record.visualAcuity?.right?.ucva || 'N/A'}, VACGL: {record.visualAcuity?.right?.vacgl || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Visual Acuity (Left)</strong></TableCell>
                            <TableCell>UCVA: {record.visualAcuity?.left?.ucva || 'N/A'}, VACGL: {record.visualAcuity?.left?.vacgl || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Recommendations</strong></TableCell>
                            <TableCell>{record.recommendations || 'None'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Remarks</strong></TableCell>
                            <TableCell>{record.optometristRemarks || 'None'}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography color="text.secondary">No previous optometry records</Typography>
              )}

              {/* Consultations */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Previous Consultations</Typography>
              {patientHistory.consultations?.length > 0 ? (
                patientHistory.consultations.map((consultation) => (
                  <Accordion key={consultation._id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        Visit {consultation.visit?.visitId} - {new Date(consultation.visit?.visitDate).toLocaleDateString()}
                        {consultation.doctor?.name && ` - Dr. ${consultation.doctor.name}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Diagnosis</strong></TableCell>
                            <TableCell>{consultation.diagnosis?.primary || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Treatment Plan</strong></TableCell>
                            <TableCell>{consultation.treatmentPlan || 'N/A'}</TableCell>
                          </TableRow>
                          {consultation.prescriptions?.length > 0 && (
                            <TableRow>
                              <TableCell><strong>Prescriptions</strong></TableCell>
                              <TableCell>
                                {consultation.prescriptions.map((p, i) => (
                                  <div key={i}>
                                    {p.medicine?.name || 'N/A'} - {p.dosage}, {p.frequency}, {p.duration}
                                  </div>
                                ))}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography color="text.secondary">No previous consultations</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OptometristPortal;
